var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { inject, PLATFORM_ID, RendererFactory2, } from "@angular/core";
import { ActivatedRoute } from "@topyourthree/activated-route";
import { Auth } from "@topyourthree/auth";
import { LocalStorage } from "@topyourthree/local-storage";
import { UI_CONFIG } from "@topyourthree/ui/ui.config";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, firstValueFrom, fromEvent, of, Subscription, } from "rxjs";
import { map, shareReplay, switchMap } from "rxjs/operators";
import { ShellConfig } from "../../app/app-shell/shell-config.service";
import * as i0 from "@angular/core";
let ThemeService = class ThemeService {
    constructor() {
        this.document = inject(DOCUMENT);
        this.localStorage = inject(LocalStorage);
        this.platformId = inject(PLATFORM_ID);
        this.renderer = inject(RendererFactory2).createRenderer(null, null);
        this.activatedRoute = inject(ActivatedRoute);
        this.auth = inject(Auth);
        this.shellConfig = inject(ShellConfig);
        this.uiConfig = inject(UI_CONFIG);
        this.DARK_MODE_MEDIA_QUERY = "(prefers-color-scheme: dark)";
        this.subscription = new Subscription();
        this.applyInitialTheme();
        if (isPlatformBrowser(this.platformId)) {
            this.themeSetting$ = combineLatest([
                this.shellConfig.getConfig(this.activatedRoute.activatedRoute),
                this.auth.isLoggedIn(),
                this.localStorage.get("theme"),
            ]).pipe(switchMap(([config, isLoggedIn, theme]) => {
                // Check for specific themes first (priority over everything)
                if (this.uiConfig.theme === "deloitte" ||
                    this.uiConfig.theme === "freightlancer") {
                    return of("light");
                }
                if (config.forceDarkMode) {
                    return of("dark");
                }
                // Allow user to override dark mode if they've selected a theme manually
                if (theme) {
                    return of(theme); // If a user has logged in before but hasn't explicitly set their theme (so they currently are defaulting to light), is the localstorage item undefined.
                }
                /*
                if (!isLoggedIn) {
                  return of('light');
                }
                  */
                return of("light");
            }), shareReplay({ bufferSize: 1, refCount: true }));
            // Listen for changes to the system theme.
            this.subscription.add(fromEvent(window.matchMedia(this.DARK_MODE_MEDIA_QUERY), "change").subscribe(async (event) => {
                if (!("matches" in event)) {
                    return;
                }
                const setting = await firstValueFrom(this.themeSetting$.pipe(untilDestroyed(this)));
                if (setting === "system") {
                    this.applyThemeToDOM(event.matches ? "dark" : "light");
                }
            }));
            // The actual theme being used in the app.
            // Derived from the themeSetting and the system theme.
            this.theme$ = this.themeSetting$.pipe(map((theme) => theme === "system"
                ? window.matchMedia(this.DARK_MODE_MEDIA_QUERY).matches
                    ? "dark"
                    : "light"
                : theme));
        }
        else {
            this.themeSetting$ = of("light");
            this.theme$ = of("light");
        }
    }
    /**
     * Return the current theme the user has selected.
     * This can be 'light' | 'dark' | 'system'.
     * You are probably looking for getTheme() instead.
     * This is to only be used when we need to know the user's actual setting.
     */
    getThemeSetting() {
        return this.themeSetting$;
    }
    /**
     * Returns the current theme being set on the app.
     * If the themeSetting is 'system', then the theme will be 'light' or 'dark' based on the user's OS settings.
     * If the themeSetting is 'light' or 'dark', then the theme will be 'light' or 'dark' respectively.
     * Use this in the app code to get the current theme.
     */
    getTheme() {
        return this.theme$;
    }
    async setThemeSetting(theme) {
        await this.localStorage.set("theme", theme);
    }
    /**
     * Applies the dark class to the DOM if the theme is dark.
     */
    applyThemeToDOM(theme) {
        if (isPlatformBrowser(this.platformId)) {
            const docElement = this.document.documentElement;
            if (theme === "dark") {
                this.renderer.addClass(docElement, "dark");
            }
            else {
                this.renderer.removeClass(docElement, "dark");
            }
        }
    }
    applyTheme(theme) {
        this.applyThemeToDOM(theme);
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    applyInitialTheme() {
        if (!isPlatformBrowser(this.platformId))
            return;
        try {
            const raw = localStorage.getItem("theme");
            if (!raw)
                return;
            const parsed = JSON.parse(raw);
            const entry = parsed["logged-out"] || parsed[Object.keys(parsed)[0]];
            if (!entry)
                return;
            const theme = entry.item; // 'light' | 'dark' | 'system'
            if (theme === "dark") {
                this.renderer.addClass(document.documentElement, "dark");
            }
            else {
                this.renderer.removeClass(document.documentElement, "dark");
            }
        }
        catch { }
    }
};
ThemeService.ɵfac = function ThemeService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ThemeService)(); };
ThemeService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ThemeService, factory: ThemeService.ɵfac, providedIn: "root" });
ThemeService = __decorate([
    UntilDestroy({ className: "ThemeService" }),
    __metadata("design:paramtypes", [])
], ThemeService);
export { ThemeService };
